/**
 * A career pilot's paint shop, as the renderer sees it.
 *
 * The career stores what the original's pilot record stores: two colour
 * choices, wings and fuselage, from `PS.EXE`'s eight-name list. The renderer
 * needs `BARON.PAL` indices. The list *is* the first eight slots of `RB.EXE`'s
 * 17-entry paint table, in menu order (docs/formats/shading-and-paint.md §10),
 * so the conversion is one lookup and no interpretation.
 *
 * `personalSchemeColours` then expands the two into the six `.CLG` group
 * colours, and `three/model-repaint.ts` applies them — which is where the
 * historical marking rules stay honoured for free: the national insignia are
 * literal palette colours that no `.CLG` group lists, so nothing the pilot can
 * pick reaches them.
 */

import { PAINT_COLORS, type CareerState, type PaintColor } from "@red-opus/engine";
import { SHIPPED_PAINT_SHOP_COLOURS, type PersonalPaint } from "@red-opus/formats";

/** The `BARON.PAL` index a paint-shop colour name resolves to, or `undefined` for a name off the list. */
export function paintColorIndex(color: PaintColor | undefined): number | undefined {
  if (color === undefined) return undefined;
  const slot = PAINT_COLORS.indexOf(color);
  return slot < 0 ? undefined : SHIPPED_PAINT_SHOP_COLOURS[slot]?.index;
}

/** Every colour the original offers, paired with the palette index it paints. */
export const PAINT_SWATCHES: readonly { readonly color: PaintColor; readonly index: number }[] = PAINT_COLORS.map(
  (color, slot) => ({ color, index: SHIPPED_PAINT_SHOP_COLOURS[slot]?.index ?? 0 }),
);

/**
 * What the pilot's personal aircraft should be drawn in, or `undefined` when he
 * has none or has never painted it — in which case the factory scheme the model
 * was baked in is already right and nothing needs repainting.
 */
export function careerPaint(career: CareerState): PersonalPaint | undefined {
  const held = career.personalAircraft;
  if (!held) return undefined;
  const wings = paintColorIndex(held.wings);
  const fuselage = paintColorIndex(held.fuselage);
  if (wings === undefined && fuselage === undefined) return undefined;
  return {
    ...(wings === undefined ? {} : { wings }),
    ...(fuselage === undefined ? {} : { fuselage }),
  };
}
